import AutoComplete from "components/AutoComplete";
import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import ImageWIthSignedUrl from "components/S3Utility/ImageWIthSignedUrl";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";
import authHeader from "services/auth-header";
import API from "services/axios";

export default function AttUserDetail({
  formData,
  setFormData,
  handleChange,
  refreshFunc,
}) {
  const dispatch = useDispatch();

  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  /*   All States
   ********************************************* */
  let inputFields = [
    { name: "User Name", value: "name", type: "text" },
    { name: "Card NO#", value: "cardno", type: "text" },
    { name: "Department", value: "department", type: "autocomplete" },
    { name: "Badge NO#", value: "badgeNo", type: "text" },
    { name: "Joining Date", value: "joiningDate", type: "date" },
    { name: "Exit Date", value: "exitDate", type: "date" },
  ];

  let contactFields = [
    { name: "Email", value: "email", type: "email" },
    { name: "Phone", value: "phone", type: "tel" },
  ];

  const [previewImage, setPreviewImage] = useState(null);

  /*   All Functions
   ********************************************* */

  const [loading, setLoading] = useState(false);

  const loadingOn = () => {
    setLoading(true);
  };
  const loadingOff = () => {
    setLoading(false);
  };

  const DbSearchFunction = async (searchText, searchField) => {
    try {
      loadingOn();

      let deviceId = params?.deviceId
        ? params?.deviceId
        : searchParams?.get("deviceId");
      if (!deviceId) {
        dispatch(
          setNotification({
            message: "Please select a device",
            type: "error",
          })
        );
        return;
      }

      if (!searchField) {
        dispatch(
          setNotification({
            message: "Please enter search text and select a field",
            type: "error",
          })
        );
        return;
      }

      let body = {
        searchText: searchText || null,
        searchField: searchField,
        deviceId,
      };

      const { data } = await API.post("/device-users-search", body, {
        headers: authHeader(),
      });

      if (data?.success) {
        return data?.data;
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    } finally {
      loadingOff();
    }
    return [];
  };

  const handleAutoCompleteChange = (key, value) => {
    setFormData((prev) => {
      return { ...prev, [key]: value };
    });
  };

  const deleteProfilePic = async () => {
    try {
      loadingOn();
      const { data } = await API.post(
        "/device-user-pic-delete/" + formData?.id,
        { folder: formData?.picture },
        {
          headers: authHeader(),
        }
      );
      if (data?.success) {
        setFormData((prev) => ({ ...prev, picture: null }));
        await refreshFunc();
        dispatch(
          setNotification({
            message: data?.message,
            type: "success",
          })
        );
        setPreviewImage(null);
      }

      loadingOff();
    } catch (err) {
      loadingOff();
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    }
  };

  const getBase64Image = async (e) => {
    const file = e.target.files[0];
    setFormData((prev) => {
      return { ...prev, file };
    });
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => {
      setPreviewImage((prev) => reader?.result);
    };
  };

  /*   All UseEffects
   ********************************************* */

  return (
    <div className="container-fluid p-0">
      {loading && <Spinner />}

      {/* 1st Row - User Information */}
      <div className="d-flex align-items-stretch justify-content-between gap-2">
        <div className="w-50 card mb-2 shadow-sm">
          <div className="card-header py-1 bg-light">
            <h5 className="">User Information</h5>
          </div>
          <div className="card-body">
            <div className="row">
              {inputFields.map((field, index) => (
                <div key={field.value} className="col-md-6 mb-3">
                  <label className="font-weight-bold">{field.name}</label>
                  {field?.type === "autocomplete" ? (
                    <AutoComplete
                      fieldName={field.value}
                      value={formData[field.value] || ""}
                      handleChange={handleAutoCompleteChange}
                      searchFunction={DbSearchFunction}
                      classNames={"form-control"}
                      placeholder={`Enter ${field.name}`}
                    />
                  ) : (
                    <input
                      name={field.value}
                      type={field.type}
                      maxLength={field.value === "name" && 24}
                      required={
                        field.value === "cardno" || field.value === "name"
                      }
                      placeholder={`Enter ${field.name}`}
                      value={formData[field.value] || ""}
                      onChange={handleChange}
                      className="form-control"
                    />
                  )}
                </div>
              ))}

              {/* Status */}
              <div className="col-md-6 mb-3">
                <label className="font-weight-bold">Status</label>
                <select
                  name="isActive"
                  value={formData?.isActive ? "active" : "inactive" || ""}
                  onChange={(e) => {
                    handleChange(e);
                    setFormData((prev) => ({
                      ...prev,
                      isActive: e.target.value === "active" ? true : false,
                    }));
                  }}
                  className="form-control custom-select"
                >
                  <option value="">Select Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              {/* authorizedAdmin true / false */}
              <div className="col-md-6 mb-3">
                <label className="font-weight-bold">Authorized Admin</label>
                <select
                  name="authorizedAdmin"
                  value={
                    formData?.authorizedAdmin === true ? "true" : "false" || ""
                  }
                  onChange={(e) => {
                    handleChange(e);
                    setFormData((prev) => ({
                      ...prev,
                      authorizedAdmin: e.target.value === "true" ? true : false,
                    }));
                  }}
                  className="form-control custom-select"
                >
                  <option value="">Select Authorized Admin</option>
                  <option value="true">True</option>
                  <option value="false">False</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="w-50 card mb-2 shadow-sm">
          <div className="card-header py-1 bg-light">
            <h5 className="">Contact Information</h5>
          </div>
          <div className="card-body">
            <div className="row">
              {contactFields.map((field, index) => (
                <div key={field.value} className="col-md-6 mb-3">
                  <label className="font-weight-bold">{field.name}</label>
                  <input
                    name={field.value}
                    type={field.type}
                    placeholder={`Enter ${field.name}`}
                    value={formData[field.value] || ""}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
              ))}

              <div key={"address"} className="col-md-12 mb-3">
                <label className="font-weight-bold">Address</label>
                <input
                  name={"address"}
                  type={"text"}
                  placeholder={`Enter Address`}
                  value={formData["address"] || ""}
                  onChange={handleChange}
                  className="form-control"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 2nd Row - Profile Picture */}
      <div className="d-flex align-items-stretch justify-content-between gap-2">
        {/* Picture */}
        <div className="w-50 card shadow-sm ">
          <div className="card-header py-1 bg-light">
            <h5 className="">User Photo</h5>
          </div>
          <div className="card-body text-center">
            <div className="mb-3">
              {formData?.picture ? (
                <div className="profile-image-container">
                  <ImageWIthSignedUrl
                    path={formData?.picture}
                    width={200}
                    height={200}
                    alt={"profile photo"}
                    className="img-thumbnail"
                  />
                </div>
              ) : (
                <div className="profile-image-container border p-1">
                  <img
                    src={
                      previewImage ? previewImage : "https://placehold.co/200"
                    }
                    width={200}
                    height={200}
                    alt=""
                    className="img-thumbnail"
                  />
                </div>
              )}
            </div>

            {(formData?.picture || previewImage) && (
              <button
                type="button"
                className="btn btn-danger mb-3"
                onClick={(e) => {
                  if (!formData?.picture) {
                    setPreviewImage(null);
                    return;
                  }
                  deleteProfilePic();
                }}
              >
                <i className="fa fa-trash mr-2"></i>
                Remove Photo
              </button>
            )}

            <div className="custom-file">
              <input
                type="file"
                name="file"
                className="form-control"
                onChange={getBase64Image}
              />
              <small className="form-text text-muted mt-2">
                Supported formats: JPG, PNG.
              </small>
            </div>
          </div>
        </div>
        {/* Notes */}
        <div className="w-50 card shadow-sm ">
          <div className="card-header py-1 bg-light">
            <h5 className="">Additional Notes</h5>
          </div>
          <div className="card-body">
            <textarea
              name="notes"
              value={formData?.notes || ""}
              onChange={handleChange}
              className="form-control"
              placeholder="Enter any notes for this user"
              rows="10"
            ></textarea>
          </div>
        </div>
      </div>
    </div>
  );
}
